import { ref, computed, type Ref } from 'vue'
import type { PortfolioProject } from '../firebase/portfolio'

/**
 * Normalisiert einen Tag-String (trimmt und entfernt Leerzeichen)
 */
const normalizeTag = (tag: string): string => {
  return tag.trim()
}

/**
 * Vergleicht zwei Tags case-insensitive
 */
const tagsEqual = (tag1: string, tag2: string): boolean => {
  return normalizeTag(tag1).toLowerCase() === normalizeTag(tag2).toLowerCase()
}


/**
 * Composable für das Filtern, Suchen und Sortieren von Portfolio-Projekten.
 * 
 * @param projects Ref der ursprünglichen Projektliste
 */
export function useProjectFilter(projects: Ref<PortfolioProject[]>) {
  const searchQuery = ref('')
  const selectedTags = ref<string[]>([])

  // Extrahiert alle verfügbaren Technologien als Tags (case-insensitive dedupliziert)
  const availableTags = computed(() => {
    // Sammle alle Tags aus allen Projekten
    const allTags: string[] = []
    projects.value.forEach(p => {
      p.technologies.forEach(t => {
        const normalized = normalizeTag(t)
        if (normalized) {
          allTags.push(normalized)
        }
      })
    })
    
    // Erstelle eine Map für case-insensitive Deduplizierung
    const tagMap = new Map<string, string>()
    
    allTags.forEach(tag => {
      const lowerKey = tag.toLowerCase()
      // Wenn noch nicht vorhanden, füge hinzu
      // Wenn bereits vorhanden, behalte die Variante mit Großbuchstaben (z.B. "WordPress" statt "wordpress")
      if (!tagMap.has(lowerKey)) {
        tagMap.set(lowerKey, tag)
      } else {
        const existingTag = tagMap.get(lowerKey)!
        // Bevorzuge die Variante mit Großbuchstaben am Anfang
        const currentStartsWithCapital = /^[A-Z]/.test(tag)
        const existingStartsWithCapital = /^[A-Z]/.test(existingTag)
        
        if (currentStartsWithCapital && !existingStartsWithCapital) {
          tagMap.set(lowerKey, tag)
        }
      }
    })
    
    // Konvertiere zurück zu Array und sortiere
    return Array.from(tagMap.values()).sort((a, b) => a.localeCompare(b))
  })

  // Gibt die gefilterte und sortierte Liste zurück
  const filteredProjects = computed(() => {
    let result = [...projects.value]

    // 1. Suche (Titel, Beschreibung, Technologien)
    if (searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase().trim()
      result = result.filter(p => 
        p.title.toLowerCase().includes(q) || 
        p.description.toLowerCase().includes(q) ||
        p.technologies.some(t => t.toLowerCase().includes(q))
      )
    }

    // 2. Tag-Filter (AND-Logik: alle ausgewählten Tags müssen vorhanden sein, case-insensitive)
    if (selectedTags.value.length > 0) {
      result = result.filter(p => 
        selectedTags.value.every(selectedTag => 
          p.technologies.some(projectTag => tagsEqual(projectTag, selectedTag))
        )
      )
    }

    // 3. Sortierung: 
    // - Zuerst Favoriten nach favoriteOrder (behält Reihenfolge aus Admin-Seite bei)
    // - Dann reguläre Projekte nach regularOrder (behält Reihenfolge aus Admin-Seite bei)
    return result.sort((a, b) => {
      // Favoriten vs. Regulär: Favoriten kommen immer zuerst
      if (a.isFavorite && !b.isFavorite) return -1
      if (!a.isFavorite && b.isFavorite) return 1
      
      // Innerhalb der Favoriten: Sortiere nach favoriteOrder
      if (a.isFavorite && b.isFavorite) {
        const aOrder = a.favoriteOrder ?? Number.MAX_SAFE_INTEGER
        const bOrder = b.favoriteOrder ?? Number.MAX_SAFE_INTEGER
        return aOrder - bOrder
      }
      
      // Innerhalb der regulären Projekte: Sortiere nach regularOrder
      const aOrder = a.regularOrder ?? Number.MAX_SAFE_INTEGER
      const bOrder = b.regularOrder ?? Number.MAX_SAFE_INTEGER
      return aOrder - bOrder
    })
  })

  const toggleTag = (tag: string) => {
    // Finde den Index case-insensitive
    const index = selectedTags.value.findIndex(t => tagsEqual(t, tag))
    if (index === -1) {
      selectedTags.value.push(tag)
    } else {
      selectedTags.value.splice(index, 1)
    }
  }

  const isTagSelected = (tag: string) => {
    return selectedTags.value.some(t => tagsEqual(t, tag))
  }

  const resetFilters = () => {
    searchQuery.value = ''
    selectedTags.value = []
  }

  return {
    searchQuery,
    selectedTags,
    availableTags,
    filteredProjects,
    toggleTag,
    isTagSelected,
    resetFilters
  }
}

